import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import styled from 'styled-components';
import { onAuthStateChanged, onIdTokenChanged, type Auth, type User } from 'firebase/auth';

import { useAuth } from '../firebase/FirebaseContext';
import { signOutUser } from './accountAuth';
import { AuthGateContext } from './AuthGateContext';
import { useAuthInFlux } from './useAuthInFlux';
import { useTotalLikedCount } from '../hooks/useTotalLikedCount';
import { useRecipients } from '../hooks/useRecipients';
import { useAlerts, type Alert } from '../hooks/useAlerts';
import { AccountAlerts } from './AccountAlerts';
import { useEmailCaptured } from '../lib/emailCaptureState';
import { SaveBoardsSheet } from './SaveBoardsSheet';

// Sign-in nudge threshold: once the anon user has accumulated this many
// SAVED items across all recipients, the avatar shows a red dot and the
// menu surfaces a "save your boards" alert. From the boards-architecture
// proposal §3 — high enough to filter casual one-off likers, low enough
// to catch the constant-shopper ICP before they invest curation effort.
const SIGN_IN_NUDGE_THRESHOLD = 3;

interface HeaderAccountMenuProps {
  /** Override the auth instance for tests/stories. Defaults to the FirebaseProvider's auth. */
  authInstance?: Auth;
  /** Override the resolved user for stories — when set, skips the listener. */
  userOverride?: User | null;
  /** Force the dropdown open (Storybook variants). */
  defaultMenuOpen?: boolean;
  /** Override the total-liked count (story / test support — skips the live hook). */
  totalLikedCountOverride?: number;
  /** Override the recipient count for stories — when set, skips the live hook. */
  recipientCountOverride?: number;
  /** Name of the most-recent board, for the "Save your boards" capture sheet
   *  headline. Story/test override; live it comes from `useRecipients`. */
  firstRecipientNameOverride?: string;
  /** Override the "email already captured" flag (story/test — skips the live
   *  localStorage read that drives the persistent-affordance visibility). */
  emailCapturedOverride?: boolean;
  /** When true, the Sign-in pill renders as a solid clay pill with white
   *  text (Letterboxd-over-hero pattern) instead of the default white-with-
   *  clay-text. The avatar variants are unchanged — the clay/cream discs
   *  read fine on the dark top-gradient over the hero. */
  overHero?: boolean;
}

const SignInButton = styled.button<{ $overHero?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 16px;
  border-radius: 9999px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms ease, box-shadow 150ms ease;
  ${({ $overHero, theme }) =>
    $overHero
      ? `
        /* Text-only treatment on the hero — Sign in reads as a link
         * cue rather than a button affordance. The hero already has
         * a CTA pill ("Get started") doing the conversion work; the
         * top-right Sign in is for returning users. */
        border: 0;
        background: transparent;
        color: #ffffff;
        text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
        padding: 0 8px;
        &:hover { opacity: 0.85; }
      `
      : `
        border: 1px solid ${theme.color.warmBorder};
        background: #ffffff;
        color: ${theme.color.clay};
        &:hover { background: ${theme.color.creamLight}; }
      `}
`;

const Avatar = styled.button<{ $anon?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  border: 0;
  // Permanent users: clay (brand). Anon users: cream with a clay border so
  // the avatar reads as "you, but not yet signed in" — distinct from the
  // signed-in clay disc without losing the avatar primitive.
  background: ${({ theme, $anon }) =>
    $anon ? theme.color.creamLight : theme.color.clay};
  color: ${({ theme, $anon }) => ($anon ? theme.color.clay : '#ffffff')};
  ${({ $anon, theme }) =>
    $anon ? `box-shadow: inset 0 0 0 1px ${theme.color.warmBorder};` : ''}
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

// Red notification dot stacked on the avatar's top-right when the anon
// user has crossed the sign-in nudge threshold. Stays visible until they
// sign in (linkWithCredential preserves the uid, so the listener moves
// from anon → permanent and the dot disappears naturally because
// `isAnon` becomes false).
const ThresholdDot = styled.span`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: #d93025;
  border: 2px solid ${({ theme }) => theme.color.creamLight};
  pointer-events: none;
`;

const Wrapper = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 240px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  padding: 12px;
  z-index: 60;
`;

const DropdownHeader = styled.div`
  padding: 4px 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.warmBorder};
  margin-bottom: 8px;
  .name {
    font-family: ${({ theme }) => theme.font.sans};
    font-size: 14px;
    font-weight: 600;
    color: hsl(var(--foreground));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .email {
    font-family: ${({ theme }) => theme.font.sans};
    font-size: 12px;
    color: ${({ theme }) => theme.color.mutedText};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 8px;
  border: 0;
  background: transparent;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  color: hsl(var(--foreground));
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.color.creamLight}; }
`;

// The "Save your boards" conversion row (Layer 2). Clay-tinted fill +
// clay label + a chevron so it reads as the primary next step, not just
// another menu line (mirrors the mock's `.primary` row).
const PrimaryDropdownItem = styled(DropdownItem)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.color.creamLight};
  color: ${({ theme }) => theme.color.clay};
  font-weight: 600;
  margin-bottom: 4px;
  &:hover { background: ${({ theme }) => theme.color.cream}; }
  .chev {
    color: ${({ theme }) => theme.color.clay};
    font-size: 16px;
    line-height: 1;
  }
`;

function initialOf(user: User): string {
  const source = user.displayName || user.email || '';
  const ch = source.trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

const PersonIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const HeaderAccountMenu: React.FC<HeaderAccountMenuProps> = ({
  authInstance: authInstanceProp,
  userOverride,
  defaultMenuOpen = false,
  totalLikedCountOverride,
  recipientCountOverride,
  firstRecipientNameOverride,
  emailCapturedOverride,
  overHero = false,
}) => {
  // Default to the FirebaseProvider's auth; the prop override remains the
  // escape hatch for stories and tests that want to inject a stub directly.
  const ctxAuth = useAuth();
  const authInstance = authInstanceProp ?? ctxAuth;
  // Read the gate context safely so this component can render under hosts that
  // don't mount AuthGateProvider (e.g. Storybook, tests, isolated previews).
  // When absent, the Sign-in pill becomes a no-op rather than throwing.
  const gate = useContext(AuthGateContext);
  const requestSignIn = gate?.requestSignIn;
  // Live aggregate-liked count for the threshold dot. Tests/stories pass
  // an override to avoid Firestore wiring.
  const liveTotalLiked = useTotalLikedCount();
  const totalLiked = totalLikedCountOverride ?? liveTotalLiked;
  // Live recipient count — gates the "My people" dropdown item. Showing
  // it for users with zero recipients pointed them at a page whose only
  // affordance was "Add someone" → /quiz, the same destination they
  // already reach from the homepage CTA. Hide until they've actually
  // built a board.
  const { recipients } = useRecipients();
  const recipientCount = recipientCountOverride ?? recipients.length;
  const hasRecipients = recipientCount > 0;
  // Persistent "Save your boards" affordance (Layer 2). Reuses the shared
  // captured flag so a capture on the board silences this too. The board's
  // display name comes from the most-recent recipient (recipients are sorted
  // newest-first) — override for stories/tests that drive count only.
  const liveEmailCaptured = useEmailCaptured();
  const emailCaptured = emailCapturedOverride ?? liveEmailCaptured;
  const firstRecipientName = firstRecipientNameOverride ?? recipients[0]?.name;
  const firstRecipientId = recipients[0]?.recipientId;
  // The recipient's active recommendation → forms the board URL the
  // re-engagement email points at (so its ?rt= link lands where redeem runs).
  const firstRecipientRecommendationId = recipients[0]?.currentRecommendationId;
  const [saveBoardsOpen, setSaveBoardsOpen] = useState(false);
  // BE-materialized alerts (Phase 6b). Fold rendering into this menu
  // rather than maintaining a sibling bell icon — per Kate's feedback
  // 2026-05-12: "there shouldn't be a separate alert icon. there should
  // just be a red dot that appears as an alert on the avatar."
  const { alerts, count: alertCount, dismiss: dismissAlert } = useAlerts();
  // Force re-render via tick rather than `setState(user)` because Firebase
  // mutates the User object in place during `linkWithPopup` — so the listener
  // fires with the same reference React already has in state, and `setState`
  // bails out via `Object.is`. Reading `authInstance.currentUser` fresh on
  // each render avoids the bail-out entirely.
  const [, forceTick] = useReducer((x: number) => x + 1, 0);
  const [menuOpen, setMenuOpen] = useState(defaultMenuOpen);
  // True while a sign-in transition is mid-flight: the mobile-OAuth return
  // leg (redirect marker present, `getRedirectResult` finalizing), an
  // anon→permanent link, or a cross-account merge. Stories/tests that pass
  // `userOverride` drive states explicitly, so the live signal is ignored
  // there (matching how the auth listener is skipped above).
  const liveAuthInFlux = useAuthInFlux();
  const authInFlux = userOverride !== undefined ? false : liveAuthInFlux;
  // Tracks whether Firebase Auth has fired its first listener callback.
  // Before bootstrap, `authInstance.currentUser` can briefly be null
  // even for users who DO have a cached session — Firebase restores
  // from IndexedDB asynchronously. Rendering the "Sign in" pill during
  // that window causes the visible flash Kate flagged 2026-05-12.
  // We lazy-init to `true` when currentUser is already populated at
  // mount, otherwise wait for the first listener tick before deciding.
  const [authBootstrapped, setAuthBootstrapped] = useState(
    () => userOverride !== undefined || authInstance.currentUser !== null,
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (userOverride !== undefined) return; // story-driven; skip the listener.
    // Subscribe to BOTH listeners. `onIdTokenChanged` covers the in-place
    // anon→permanent link upgrade where the uid is preserved (and
    // `onAuthStateChanged` is silent). `onAuthStateChanged` covers cold-load
    // IndexedDB-restore on page refresh, which `onIdTokenChanged` sometimes
    // misses due to subscription timing during Auth init. Both feed the same
    // tick, and rendering reads `authInstance.currentUser` fresh below.
    const onFire = () => {
      setAuthBootstrapped(true);
      forceTick();
    };
    const unsubAuth = onAuthStateChanged(authInstance, onFire);
    const unsubToken = onIdTokenChanged(authInstance, onFire);
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, [authInstance, userOverride]);

  const user: User | null =
    userOverride !== undefined ? userOverride : authInstance.currentUser;

  // Close on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // Auth-bootstrap window: render a neutral placeholder avatar instead
  // of flashing "Sign in" while Firebase restores the IndexedDB-cached
  // user. Without this guard, signed-in users see a brief "Sign in"
  // flash on cold-load (Kate feedback 2026-05-12) before the listener
  // hydrates the real user. The placeholder is the same dimensions as
  // a real avatar so the header doesn't shift after bootstrap.
  if (!authBootstrapped) {
    return <Avatar type="button" aria-hidden tabIndex={-1} disabled $anon />;
  }
  // Sign-in transition mid-flight AND the current user is still anon/null —
  // rendering the anon "Sign in" UI now would read as "the sign-in didn't
  // take" (mobile-OAuth redirect return: the restored ANON session paints
  // ~1-2s before `getRedirectResult` lands the permanent user; reported
  // 2026-07-06). Hold the neutral placeholder; the moment `isAnonymous`
  // flips false the real avatar renders — a permanent user is never held
  // back by the grace window.
  if (authInFlux && (!user || user.isAnonymous)) {
    return <Avatar type="button" aria-hidden tabIndex={-1} disabled $anon />;
  }
  // Bootstrap completed AND no user → genuinely signed out. Show the
  // Sign-in pill (the right CTA for a user who hasn't authed at all).
  if (!user) {
    return (
      <SignInButton
        type="button"
        data-signin-entry="header-pill"
        $overHero={overHero}
        onClick={() => requestSignIn?.({ mode: 'signin', entry: 'header-pill' })}
      >
        Sign in
      </SignInButton>
    );
  }

  const isAnon = user.isAnonymous;

  // On the hero, render the anonymous user's account slot as a plain
  // "Sign in" text link instead of the avatar circle. The hero is a
  // conversion-first surface — an explicit sign-in cue reads cleaner
  // than the abstract avatar glyph at the same position. Permanent
  // users keep their initial avatar (they don't need a sign-in link).
  if (isAnon && overHero) {
    return (
      <SignInButton
        type="button"
        data-signin-entry="header-hero-link"
        $overHero
        onClick={() => requestSignIn?.({ mode: 'signin', entry: 'header-hero-link' })}
      >
        Sign in
      </SignInButton>
    );
  }

  const showSignInNudge = isAnon && totalLiked >= SIGN_IN_NUDGE_THRESHOLD;
  // "Save your boards" affordance shows for anon users who have a board
  // (a recipient) and haven't handed over an email yet. This is the
  // always-available Layer-2 path — it lights the same dot the sign-in
  // nudge does (once they have a board, before any save-count threshold).
  const showSaveBoards = isAnon && hasRecipients && !emailCaptured;
  // Red dot on the avatar surfaces the save-boards affordance, the sign-in
  // nudge, AND any active BE-materialized alerts. Single dot, multiple
  // causes — the user just sees "there's something for me to look at" and
  // opens the menu to find out what.
  const showThresholdDot = showSaveBoards || showSignInNudge || alertCount > 0;
  // Sign-in nudge alert is anon-only; alerts list is everyone-who-has-
  // alerts. They render in the same dropdown but their visibility gates
  // are independent.
  const showNudgeAlert = showSignInNudge;

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await signOutUser(authInstance);
    } catch (err) {
      console.error('[HeaderAccountMenu] signOut failed:', err);
    }
  };

  const handleMyPeople = () => {
    setMenuOpen(false);
    // Anchor to the homepage "Your People" section instead of a separate
    // /people page (Kate's call 2026-05-13 — single-surface IA per the
    // Spotify/Airbnb research). Plain navigation rather than `useNavigate()`
    // so this component continues to render outside a `<Router>` (e.g.
    // SiteHeader unit tests, isolated stories). When already on the
    // homepage the hash change alone is enough — the browser scrolls
    // smoothly to the section if scroll-behavior: smooth is set on html;
    // otherwise it jumps, which is fine.
    if (typeof window !== 'undefined') {
      window.location.assign('/#your-people');
    }
  };

  const handleYourSaves = () => {
    setMenuOpen(false);
    // Desktop entry point to the all-saves board (/liked) — the mobile
    // equivalent is the You page's "N saved across M people" row (the You
    // tab doesn't exist on desktop; this dropdown is its stand-in). Plain
    // navigation for the same outside-a-Router reason as handleMyPeople.
    if (typeof window !== 'undefined') {
      window.location.assign('/liked');
    }
  };

  const handleSignIn = () => {
    setMenuOpen(false);
    requestSignIn?.({ mode: 'signin', entry: 'header-dropdown' });
  };

  const handleSaveBoards = () => {
    // Close the dropdown and pop the capture sheet (SaveBoardsSheet fires the
    // `shown` GA + owns the callable/captured-flag wiring).
    setMenuOpen(false);
    setSaveBoardsOpen(true);
  };

  const handleAlertClick = (alert: Alert) => {
    setMenuOpen(false);
    // Plain navigation rather than `useNavigate()` so this component
    // continues to render outside a `<Router>` (matches handleMyPeople
    // pattern above). Cross-page jumps are fine as full reloads.
    if (typeof window !== 'undefined') {
      window.location.assign(alert.href);
    }
  };

  const avatarLabel = isAnon
    ? showSaveBoards
      ? 'Account menu — save your boards'
      : showThresholdDot
        ? `Account menu — ${totalLiked} saved, sign in to keep`
        : 'Account menu — anonymous'
    : 'Account menu';

  return (
    <Wrapper ref={wrapperRef}>
      <Avatar
        type="button"
        $anon={isAnon}
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={avatarLabel}
      >
        {/* Anon avatars get a person silhouette glyph (traditional
            avatar treatment); permanent users get their initial. Both
            render at the same size so the avatar slot doesn't reflow
            when an anon user signs in. */}
        {isAnon ? <PersonIcon /> : <span aria-hidden>{initialOf(user)}</span>}
        {showThresholdDot && <ThresholdDot data-testid="threshold-dot" />}
      </Avatar>
      {menuOpen && (
        <Dropdown role="menu">
          <AccountAlerts
            variant="dropdown"
            alerts={alerts}
            showNudge={showNudgeAlert}
            onAlertClick={handleAlertClick}
            onDismiss={dismissAlert}
          />
          {showSaveBoards && (
            <PrimaryDropdownItem
              type="button"
              role="menuitem"
              onClick={handleSaveBoards}
              data-testid="save-boards-item"
            >
              <span>Save your boards</span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </PrimaryDropdownItem>
          )}
          {!isAnon && (
            <DropdownHeader>
              <div className="name">
                {user.displayName || user.email || 'Signed in'}
              </div>
              {user.email && user.displayName && (
                <div className="email">{user.email}</div>
              )}
            </DropdownHeader>
          )}
          {hasRecipients && (
            <DropdownItem type="button" role="menuitem" onClick={handleMyPeople}>
              My people
            </DropdownItem>
          )}
          {totalLiked > 0 && (
            <DropdownItem type="button" role="menuitem" onClick={handleYourSaves}>
              Your saves · {totalLiked}
            </DropdownItem>
          )}
          {!isAnon && (
            <DropdownItem type="button" role="menuitem" onClick={() => window.location.assign('/holidays')}>
              Holidays
            </DropdownItem>
          )}
          {isAnon ? (
            <DropdownItem
              type="button"
              role="menuitem"
              data-signin-entry="header-dropdown"
              onClick={handleSignIn}
            >
              Sign in
            </DropdownItem>
          ) : (
            <DropdownItem type="button" role="menuitem" onClick={handleSignOut}>
              Log out
            </DropdownItem>
          )}
        </Dropdown>
      )}
      {/* Persistent "Save your boards" capture sheet — lives outside the
          dropdown so it stays mounted after the menu closes on tap. */}
      <SaveBoardsSheet
        open={saveBoardsOpen}
        recipientName={firstRecipientName ?? null}
        recipientId={firstRecipientId}
        recommendationId={firstRecipientRecommendationId}
        onClose={() => setSaveBoardsOpen(false)}
      />
    </Wrapper>
  );
};
